import React, { useState, useEffect, useRef } from 'react'

import { SendNoiseReport as BLSendNoiseReport } from './../../../utilities/BL/Admin/Overlays/Noisemeter'


const Noisemeter = ({ config, winners, userZone }) => {
    
    // --- ESTADOS ---
    const [gameState, setGameState] = useState('LOADING'); 
    
    const [volumeDisplay, setVolumeDisplay] = useState(0); 
    const [countdownVal, setCountdownVal] = useState(3);
    const [timeLeft, setTimeLeft] = useState(0); 
    const [winnerMessage, setWinnerMessage] = useState(null);

    // --- REFS ---
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const volumeAccumulator = useRef(0);
    const volumeReadings = useRef(0);
    
    const reportingInterval = useRef(null);
    const animationRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const gameTimerRef = useRef(null);

    // -------------------------------------------------------------------------
    // MONTAJE / DESMONTAJE
    // -------------------------------------------------------------------------
    useEffect(() => {
        initMicrophone(true); 
        return () => stopAllProcesses();
    }, []);

    // -------------------------------------------------------------------------
    // LÓGICA DE CONFIGURACIÓN Y TIEMPOS
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (!config) return;

        // --- FIX: RESETEAR MENSAJE DE GANADOR AL INICIAR NUEVA PARTIDA ---
        // Si cambia la config (nueva partida), limpiamos el ganador anterior para evitar 
        // que en la segunda partida se quede pegado el mensaje viejo o no aparezca.
        setWinnerMessage(null);

        // Validar Zona
        if (config.targetZone && config.targetZone !== 'all' && config.targetZone.toLowerCase() !== userZone?.toLowerCase()) {
            stopAllProcesses();
            setGameState('LOCKED');
            return;
        }

        if (!audioContextRef.current) {
            if (gameState !== 'LOADING') setGameState('PERMISSION_NEEDED');
            return;
        }

        handleGameTiming();

    }, [config, userZone]); // Quitamos gameState de las dependencias para evitar loops innecesarios

    
    const handleGameTiming = () => {
        const now = Date.now();
        const startAt = new Date(config.startAt).getTime();
        const endsAt = new Date(config.endsAt).getTime();
        const msUntilStart = startAt - now;

        stopAllProcesses(); // Limpieza preventiva antes de recalcular tiempos

        if (msUntilStart > 0) {
            // Countdown
            // --- FIX 1: Clamp del countdown ---
            // Si msUntilStart es 3050ms, Math.ceil da 4. Lo forzamos a máximo 3 para la UI.
            const rawSeconds = Math.ceil(msUntilStart / 1000);
            const displaySeconds = rawSeconds > 3 ? 3 : rawSeconds;
            
            startCountdown(displaySeconds, msUntilStart);
            
            // Programamos el inicio exacto
            setTimeout(() => {
                if (audioContextRef.current) startGameFlow(endsAt);
            }, msUntilStart);
        } else {
            // El juego ya debería haber empezado (o estamos entrando tarde)
            startGameFlow(endsAt);
        }
    };


    // -------------------------------------------------------------------------
    // REACCIÓN A GANADORES (Backend push)
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (!winners) return;
        
        // Detenemos todo apenas llega el ganador
        stopAllProcesses(); 
        
        // Si el estado ya era FINISHED (por tiempo), esto solo actualiza el mensaje.
        setGameState('FINISHED');
        
        const wZone = winners.winnerZone || winners.WinnerZone;
        
        if (wZone === 'tie') setWinnerMessage('¡Ha habido empate!');
        else if (wZone?.toLowerCase() === userZone?.toLowerCase()) setWinnerMessage('¡Ganaste! 🏆');
        else setWinnerMessage(`Ganó ${wZone?.toUpperCase()}`);

    }, [winners, userZone]);


    // -------------------------------------------------------------------------
    // AUDIO ENGINE
    // -------------------------------------------------------------------------
    const initMicrophone = async (isAutoInit = false) => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const ctx = new AudioContext();
            audioContextRef.current = ctx;
            
            const stream = await navigator.mediaDevices.getUserMedia({ audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: false
            } });
            const source = ctx.createMediaStreamSource(stream);
            
            analyserRef.current = ctx.createAnalyser();
            analyserRef.current.fftSize = 256; 
            analyserRef.current.smoothingTimeConstant = 0.5;

            const filter = ctx.createBiquadFilter();
            filter.type = 'bandpass';
            filter.frequency.value = 1000; 

            source.connect(filter);
            filter.connect(analyserRef.current);

            if (ctx.state === 'suspended') {
                await ctx.resume();
            }

            if (config) {
                handleGameTiming();
            } else {
                setGameState('WAITING_SIGNAL');
            }

        } catch (err) {
            console.warn("No se pudo iniciar micrófono automáticamente:", err);
            setGameState('PERMISSION_NEEDED');
            if (!isAutoInit) {
                alert("Es necesario activar el micrófono para jugar.");
            }
        }
    };

    // -------------------------------------------------------------------------
    // TIMERS Y LOOPS
    // -------------------------------------------------------------------------
    const startCountdown = (startSeconds, msTotal) => {
        setGameState('COUNTDOWN');
        setCountdownVal(startSeconds);
        
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        
        // Sincronizamos el decremento visual
        let current = startSeconds;
        countdownIntervalRef.current = setInterval(() => {
            current--;
            if (current > 0) setCountdownVal(current);
            else clearInterval(countdownIntervalRef.current);
        }, 1000);
    };

    const startGameTimer = (endsAtTimestamp) => {
        if (gameTimerRef.current) clearInterval(gameTimerRef.current);
        
        const updateTimer = () => {
            const now = Date.now();
            const diff = Math.ceil((endsAtTimestamp - now) / 1000);
            
            setTimeLeft(diff > 0 ? diff : 0);

            // --- FIX 2 & 3: Manejo del fin de tiempo local ---
            if (diff <= 0) {
                clearInterval(gameTimerRef.current);
                
                // 1. Detener reporte de red inmediatamente (Fix Issue 3)
                stopAllProcesses(); 

                // 2. Cambiar UI a finalizado aunque no tengamos ganador aún (Fix Issue 2)
                setGameState('FINISHED');
                
                // 3. Mensaje temporal mientras llega 'winners'
                // Si 'winners' llega rápido, esto se sobrescribe casi instantáneamente
                setWinnerMessage((prev) => prev || "Calculando ganador..."); 
            }
        };
        
        updateTimer();
        gameTimerRef.current = setInterval(updateTimer, 1000);
    };

    const startGameFlow = (endsAtTimestamp) => {
        if (!analyserRef.current) return;

        // Doble check por si el tiempo ya expiró antes de empezar
        if (Date.now() >= endsAtTimestamp) {
            setGameState('FINISHED');
            setWinnerMessage("Tiempo finalizado");
            return;
        }

        setGameState('RUNNING');
        startGameTimer(endsAtTimestamp);
        processAudioLoop();
        
        if (reportingInterval.current) clearInterval(reportingInterval.current);
        reportingInterval.current = setInterval(sendReport, 500);
    };

    const processAudioLoop = () => {
        if (!analyserRef.current) return;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
        let average = sum / dataArray.length;
        
        let visualVal = Math.max(0, (average - 15)) * 2.0; 
        if (visualVal > 100) visualVal = 100;
        
        setVolumeDisplay(visualVal);
        volumeAccumulator.current += visualVal; 
        volumeReadings.current += 1;
        
        animationRef.current = requestAnimationFrame(processAudioLoop);
    };

    const sendReport = () => {
        // Validación extra: Si el juego no está corriendo, no enviar nada.
        if (volumeReadings.current === 0 || !config?.matchId) return;
        
        const avgVolume = volumeAccumulator.current / volumeReadings.current;
        
        // Se asume que BLSendNoiseReport existe en el ámbito
        if(typeof BLSendNoiseReport === 'function') {
             BLSendNoiseReport(config.matchId, userZone, avgVolume);
        }
       
        volumeAccumulator.current = 0;
        volumeReadings.current = 0;
    };

    const stopAllProcesses = () => {
        // --- FIX 3: Esta función ahora asegura que no queden zombies ---
        if (reportingInterval.current) {
            clearInterval(reportingInterval.current);
            reportingInterval.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (gameTimerRef.current) {
            clearInterval(gameTimerRef.current);
            gameTimerRef.current = null;
        }
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }
    };

    // -------------------------------------------------------------------------
    // RENDER
    // -------------------------------------------------------------------------
    return (
        <div className="noisemeter">
            <div className="noisemeter__card">
                
                {gameState === 'LOADING' && (
                    <div className="noisemeter__step">
                        <div className="spinner-border text-primary " role="status"></div>
                        <p className='Noisemeter__Fan__Text--Center'>Verificando micrófono...</p>
                    </div>
                )}

                {gameState === 'PERMISSION_NEEDED' && (
                    <div className="noisemeter__step">
                        <p className='Noisemeter__Fan__Text--Danger Noisemeter__Fan__Text--Big Noisemeter__Fan__Text--Center Noisemeter__Fan__Element--ZeroBottom'>
                            <i className="bi bi-mic-mute-fill"></i>
                        </p>
                        <p className="Noisemeter__Fan__Text--Center">Permiso denegado</p>
                        <p className="Noisemeter__Fan__Text--Center">Entraste tarde o no diste permiso antes.</p>
                        <p className="Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Highlight">Actívalo ahora para unirte al juego</p>
                        <button className="FrontPageNoisemeter__Fan__Element--Center FrontPageNoisemeter__Fan__Button Btn__Adsl--Red" onClick={() => initMicrophone(false)}>
                            <i className="bi bi-mic-fill"></i> Activar micrófono ahora
                        </button>
                    </div>
                )}

                {gameState === 'WAITING_SIGNAL' && (
                    <div className="noisemeter__step noisemeter__step--waiting">
                        <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Big'><i className="bi bi-broadcast-pin"></i></p>
                        <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Success'>¡Estás listo!</p>
                        <p className='Noisemeter__Fan__Text--Center'>Esperando señal de inicio...</p>
                    </div>
                )}

                {gameState === 'LOCKED' && (
                    <div className="noisemeter__step">
                        <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Big Noisemeter__Fan__Element--ZeroBottom'>
                            <i className="bi bi-lock-fill" style={{ color: '#999' }}></i>
                        </p>
                        <p className='Noisemeter__Fan__Text--Center'>Esperando tu turno.</p>
                        <p className='Noisemeter__Fan__Text--Center'>Por ahora está jugando otra zona</p>
                    </div>
                )}

                {gameState === 'COUNTDOWN' && (
                    <div className="noisemeter__step">
                        <p className="Noisemeter__Fan__Text--Danger Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Medium">Grita en...</p>
                        <span className="noisemeter__countdown-number Noisemeter__Fan__Text--Danger">{countdownVal}</span>
                    </div>
                )}

                {gameState === 'RUNNING' && (
                    <div className="noisemeter__step">
                        <p className="Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Highlight Noisemeter__Fan__Text--ZeroBottom Noisemeter__Fan__Text--Medium">¡Grita ahora!</p>
                        <div className="noisemeter__timer" style={{fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '15px', color: '#555'}}>
                            <i className="bi bi-stopwatch-fill"></i> {timeLeft}s
                        </div>
                        <div className="noisemeter__meter-container Noisemeter__Fan__Element--ZeroBottom">
                            <div className={`noisemeter__meter-fill`} style={{ width: `${volumeDisplay}%`, background: `linear-gradient(90deg, #4caf50, ${volumeDisplay > 70 ? '#f44336' : '#ffeb3b'})`}}></div>
                        </div>
                        <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Small'>Intensidad de tu grito</p>
                    </div>
                )}

                {gameState === 'FINISHED' && (
                    <div className="noisemeter__step">
                        <p className=''>¡Tiempo finalizado!</p>
                        <div className=''>
                            <p className='Noisemeter__Fan__Text--Highlight Noisemeter__Fan__Text--Medium'>
                                {winnerMessage || "Calculando..."}
                            </p>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export { Noisemeter };





















// const Noisemeter = ({ config, winners, userZone }) => {
    
//     // --- ESTADOS ---
//     // Inicializamos en 'LOADING' para intentar la conexión automática antes de mostrar nada
//     const [gameState, setGameState] = useState('LOADING'); 
    
//     const [volumeDisplay, setVolumeDisplay] = useState(0); 
//     const [countdownVal, setCountdownVal] = useState(3);
//     const [timeLeft, setTimeLeft] = useState(0); 
//     const [winnerMessage, setWinnerMessage] = useState(null);

//     // --- REFS ---
//     const audioContextRef = useRef(null);
//     const analyserRef = useRef(null);
//     const volumeAccumulator = useRef(0);
//     const volumeReadings = useRef(0);
    
//     const reportingInterval = useRef(null);
//     const animationRef = useRef(null);
//     const countdownIntervalRef = useRef(null);
//     const gameTimerRef = useRef(null);

//     useEffect(() => {
//         // INTENTO AUTOMÁTICO DE INICIO (AUTO-JOIN)
//         // Al montar, intentamos iniciar el micrófono silenciosamente.
//         // Si el usuario ya dio permiso en FrontPageNoisemeter, esto funcionará sin user gesture.
//         initMicrophone(true); 

//         return () => stopAllProcesses();
//     }, []);

//     // -------------------------------------------------------------------------
//     // LÓGICA DE CONFIGURACIÓN Y TIEMPOS
//     // -------------------------------------------------------------------------
//     useEffect(() => {
//         if (!config) return;

//         // Validar Zona
//         if (config.targetZone && config.targetZone !== 'all' && config.targetZone.toLowerCase() !== userZone?.toLowerCase()) {
//             stopAllProcesses();
//             setGameState('LOCKED');
//             return;
//         }

//         // Si aún no tenemos AudioContext (falló el auto-init), nos quedamos en PERMISSION_NEEDED
//         // y esperamos a que el usuario le de clic al botón.
//         if (!audioContextRef.current) {
//             if (gameState !== 'LOADING') setGameState('PERMISSION_NEEDED');
//             return;
//         }

//         // Si YA tenemos AudioContext, gestionamos el flujo del juego
//         handleGameTiming();

//     }, [config, userZone, gameState]); // Agregamos gameState para re-evaluar si cambia

    
//     const handleGameTiming = () => {
//         const now = Date.now();
//         const startAt = new Date(config.startAt).getTime();
//         const endsAt = new Date(config.endsAt).getTime();
//         const msUntilStart = startAt - now;

//         if (msUntilStart > 0) {
//             // Countdown
//             startCountdown(Math.ceil(msUntilStart / 1000));
            
//             setTimeout(() => {
//                 // Doble chequeo de referencia por si desmontó
//                 if (audioContextRef.current) startGameFlow(endsAt);
//             }, msUntilStart);
//         } else {
//             // Juego corriendo
//             startGameFlow(endsAt);
//         }
//     };


//     // -------------------------------------------------------------------------
//     // REACCIÓN A GANADORES
//     // -------------------------------------------------------------------------
//     useEffect(() => {
//         if (!winners) return;
//         stopAllProcesses();
//         setGameState('FINISHED');
//         const wZone = winners.winnerZone || winners.WinnerZone;
        
//         if (wZone === 'tie') setWinnerMessage('¡Ha habido empate!');
//         else if (wZone?.toLowerCase() === userZone?.toLowerCase()) setWinnerMessage('¡Ganaste! 🏆');
//         else setWinnerMessage(`Ganó ${wZone?.toUpperCase()}`);
//     }, [winners, userZone]);


//     // -------------------------------------------------------------------------
//     // AUDIO ENGINE
//     // -------------------------------------------------------------------------
    
//     /**
//      * @param {boolean} isAutoInit - Si es true, suprime el 'alert' de error y manda a UI de permiso.
//      */
//     const initMicrophone = async (isAutoInit = false) => {
//         try {
//             const AudioContext = window.AudioContext || window.webkitAudioContext;
//             const ctx = new AudioContext();
//             audioContextRef.current = ctx;
            
//             //const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
//             const stream = await navigator.mediaDevices.getUserMedia({ audio: {
//                 echoCancellation: true,
//                 noiseSuppression: true,
//                 autoGainControl: false
//             } });
//             const source = ctx.createMediaStreamSource(stream);
            
//             analyserRef.current = ctx.createAnalyser();
//             analyserRef.current.fftSize = 256; 
//             analyserRef.current.smoothingTimeConstant = 0.5;

//             const filter = ctx.createBiquadFilter();
//             filter.type = 'bandpass';
//             filter.frequency.value = 1000; 

//             source.connect(filter);
//             filter.connect(analyserRef.current);

//             // FIX: En algunos navegadores, si se crea sin gesto, inicia en 'suspended'.
//             if (ctx.state === 'suspended') {
//                 await ctx.resume();
//             }

//             // ÉXITO: Si hay config, lanzamos la lógica de tiempos. Si no, esperamos señal.
//             if (config) {
//                 handleGameTiming();
//             } else {
//                 setGameState('WAITING_SIGNAL');
//             }

//         } catch (err) {
//             console.warn("No se pudo iniciar micrófono automáticamente:", err);
            
//             // FALLO:
//             // Si era automático (carga inicial), mostramos la UI para pedir permiso manual.
//             // Si fue manual (clic del botón), mostramos alerta.
//             setGameState('PERMISSION_NEEDED');
//             if (!isAutoInit) {
//                 alert("Es necesario activar el micrófono para jugar.");
//             }
//         }
//     };

//     // -------------------------------------------------------------------------
//     // TIMERS Y LOOPS
//     // -------------------------------------------------------------------------
//     const startCountdown = (seconds) => {
//         setGameState('COUNTDOWN');
//         setCountdownVal(seconds);
//         if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
//         let current = seconds;
//         countdownIntervalRef.current = setInterval(() => {
//             current--;
//             setCountdownVal(current);
//             if (current <= 0) clearInterval(countdownIntervalRef.current);
//         }, 1000);
//     };

//     const startGameTimer = (endsAtTimestamp) => {
//         if (gameTimerRef.current) clearInterval(gameTimerRef.current);
//         const updateTimer = () => {
//             const now = Date.now();
//             const diff = Math.ceil((endsAtTimestamp - now) / 1000);
//             setTimeLeft(diff > 0 ? diff : 0);
//             if (diff <= 0) clearInterval(gameTimerRef.current);
//         };
//         updateTimer();
//         gameTimerRef.current = setInterval(updateTimer, 1000);
//     };

//     const startGameFlow = (endsAtTimestamp) => {
//         if (!analyserRef.current) return;
//         setGameState('RUNNING');
//         if (endsAtTimestamp) startGameTimer(endsAtTimestamp);
//         processAudioLoop();
//         if (reportingInterval.current) clearInterval(reportingInterval.current);
//         reportingInterval.current = setInterval(sendReport, 500);
//     };

//     const processAudioLoop = () => {
//         if (!analyserRef.current) return;
//         const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
//         analyserRef.current.getByteFrequencyData(dataArray);
//         let sum = 0;
//         for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
//         let average = sum / dataArray.length;
        
//         let visualVal = Math.max(0, (average - 15)) * 2.0; 
//         if (visualVal > 100) visualVal = 100;
        
//         setVolumeDisplay(visualVal);
//         volumeAccumulator.current += visualVal; 
//         volumeReadings.current += 1;
//         animationRef.current = requestAnimationFrame(processAudioLoop);
//     };

//     const sendReport = () => {
//         if (volumeReadings.current === 0 || !config?.matchId) return;
//         const avgVolume = volumeAccumulator.current / volumeReadings.current;
//         BLSendNoiseReport(config.matchId, userZone, avgVolume);
//         volumeAccumulator.current = 0;
//         volumeReadings.current = 0;
//     };

//     const stopAllProcesses = () => {
//         if (reportingInterval.current) clearInterval(reportingInterval.current);
//         if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
//         if (gameTimerRef.current) clearInterval(gameTimerRef.current);
//         if (animationRef.current) cancelAnimationFrame(animationRef.current);
//     };

//     // -------------------------------------------------------------------------
//     // RENDER
//     // -------------------------------------------------------------------------
//     return (
//         <div className="noisemeter">
//             <div className="noisemeter__card">
                
//                 {gameState === 'LOADING' && (
//                     <div className="noisemeter__step">
//                         <div className="spinner-border text-primary " role="status"></div>
//                         <p className='Noisemeter__Fan__Text--Center'>Verificando micrófono...</p>
//                     </div>
//                 )}

//                 {gameState === 'PERMISSION_NEEDED' && (
//                     <div className="noisemeter__step">
//                         <p className='Noisemeter__Fan__Text--Danger Noisemeter__Fan__Text--Big Noisemeter__Fan__Text--Center Noisemeter__Fan__Element--ZeroBottom'>
//                             <i className="bi bi-mic-mute-fill"></i>
//                         </p>
//                         <p className="Noisemeter__Fan__Text--Center">Permiso denegado</p>
//                         <p className="Noisemeter__Fan__Text--Center">Entraste tarde o no diste permiso antes.</p>
//                         <p className="Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Highlight">Actívalo ahora para unirte al juego</p>
//                         <button className="FrontPageNoisemeter__Fan__Element--Center FrontPageNoisemeter__Fan__Button Btn__Adsl--Red" onClick={() => initMicrophone(false)}>
//                             <i className="bi bi-mic-fill"></i> Activar micrófono ahora
//                         </button>
//                     </div>
//                 )}

//                 {gameState === 'WAITING_SIGNAL' && (
//                     <div className="noisemeter__step noisemeter__step--waiting">
//                         <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Big'><i class="bi bi-broadcast-pin"></i></p>
//                         <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Success'>¡Estás listo!</p>
//                         <p className='Noisemeter__Fan__Text--Center'>Esperando señal de inicio...</p>
//                     </div>
//                 )}

//                 {gameState === 'LOCKED' && (
//                     <div className="noisemeter__step">
//                         <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Big Noisemeter__Fan__Element--ZeroBottom'>
//                             <i className="bi bi-lock-fill" style={{ color: '#999' }}></i>
//                         </p>
//                         <p className='Noisemeter__Fan__Text--Center'>Esperando tu turno.</p>
//                         <p className='Noisemeter__Fan__Text--Center'>Por ahora está jugando otra zona</p>
//                     </div>
//                 )}

//                 {gameState === 'COUNTDOWN' && (
//                     <div className="noisemeter__step">
//                         <p className="Noisemeter__Fan__Text--Danger Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Medium">Grita en...</p>
//                         <span className="noisemeter__countdown-number Noisemeter__Fan__Text--Danger">{countdownVal}</span>
//                     </div>
//                 )}

//                 {gameState === 'RUNNING' && (
//                     <div className="noisemeter__step">
//                         <p className="Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Highlight Noisemeter__Fan__Text--ZeroBottom Noisemeter__Fan__Text--Medium">¡Grita ahora!</p>
//                         <div className="noisemeter__timer" style={{fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '15px', color: '#555'}}>
//                             <i class="bi bi-stopwatch-fill"></i> {timeLeft}s
//                         </div>
//                         <div className="noisemeter__meter-container Noisemeter__Fan__Element--ZeroBottom">
//                             <div className={`noisemeter__meter-fill`} style={{ width: `${volumeDisplay}%`, background: `linear-gradient(90deg, #4caf50, ${volumeDisplay > 70 ? '#f44336' : '#ffeb3b'})`}}></div>
//                         </div>
//                         <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Small'>Intensidad de tu grito</p>
//                         {/* <span className="Noisemeter__Fan__Text--Highlight Noisemeter__Fan__Text--Medium">{Math.round(volumeDisplay)}</span> */}
//                     </div>
//                 )}

//                 {gameState === 'FINISHED' && (
//                     <div className="noisemeter__step">
//                         <p className=''>¡Tiempo finalizado!</p>
//                         <div className=''>
//                             <p className='Noisemeter__Fan__Text--Highlight Noisemeter__Fan__Text--Medium'>
//                                 {winnerMessage || "Calculando..."}
//                             </p>
//                         </div>
//                     </div>
//                 )}

//             </div>
//         </div>
//     );
// };

// export { Noisemeter };





















// /**
//  * Componente Fan - Ruidómetro
//  * Maneja el micrófono, el feedback visual local y el envío de datos al servidor.
//  * * Props:
//  * @param {object} config - Datos de la partida (matchId, startAt, endsAt, targetZone).
//  * @param {object} winners - Datos del ganador cuando termina (winnerZone).
//  * @param {string} userZone - Zona actual del usuario (ej: "azul", "tecate").
//  */
// const Noisemeter = ({ config, winners, userZone }) => {
    
//     // --- ESTADOS DE LA INTERFAZ ---
//     // 'PERMISSION_NEEDED': Botón para activar micro.
//     // 'WAITING_SIGNAL': Micro listo, esperando comando del server.
//     // 'LOCKED': La partida es para otra zona.
//     // 'COUNTDOWN': Cuenta regresiva 3..2..1.
//     // 'RUNNING': Juego activo (Gritando).
//     // 'FINISHED': Muestra resultados.
//     const [gameState, setGameState] = useState('PERMISSION_NEEDED'); 
    
//     // Valores visuales
//     const [volumeDisplay, setVolumeDisplay] = useState(0); // 0-100%
//     const [countdownVal, setCountdownVal] = useState(3);
//     const [timeLeft, setTimeLeft] = useState(0); // Tiempo restante de juego
//     const [winnerMessage, setWinnerMessage] = useState(null);

//     // --- REFERENCIAS (Para no perder valores entre renderizados) ---
//     const audioContextRef = useRef(null);
//     const analyserRef = useRef(null);
    
//     // Acumuladores para el reporte
//     const volumeAccumulator = useRef(0);
//     const volumeReadings = useRef(0);
    
//     // Intervalos y animaciones
//     const reportingInterval = useRef(null);   // Envío al servidor
//     const animationRef = useRef(null);        // Loop visual (60fps)
//     const countdownIntervalRef = useRef(null);// Cuenta regresiva inicial
//     const gameTimerRef = useRef(null);        // Reloj de juego (30s -> 0s)

//     // Limpieza de intervalos al desmontar
//     useEffect(() => {
//         return () => stopAllProcesses();
//     }, []);

//     // -------------------------------------------------------------------------
//     // 1. REACCIÓN A LA CONFIGURACIÓN (INICIO DE PARTIDA)
//     // -------------------------------------------------------------------------
//     useEffect(() => {
//         if (!config) return;

//         // A) Validar si mi zona participa
//         // Si el admin eligió una zona específica y no es la mía, me bloqueo.
//         if (config.targetZone && config.targetZone !== 'all' && config.targetZone.toLowerCase() !== userZone?.toLowerCase()) {
//             stopAllProcesses();
//             setGameState('LOCKED');
//             return;
//         }

//         // B) Sincronización de Tiempo
//         const now = Date.now();
//         const startAt = new Date(config.startAt).getTime();
//         const endsAt = new Date(config.endsAt).getTime();
//         const msUntilStart = startAt - now;

//         if (msUntilStart > 0) {
//             // Caso 1: Llegamos antes del inicio -> Programar Countdown
//             if (audioContextRef.current) {
//                 // Si ya dio permisos, mostramos countdown visual
//                 startCountdown(Math.ceil(msUntilStart / 1000));
//             } else {
//                 // Si no tiene permisos, se queda en PERMISSION_NEEDED, pero el timer corre por detrás
//                 setGameState('PERMISSION_NEEDED');
//             }
            
//             // Programar el inicio automático del flujo de juego
//             setTimeout(() => {
//                 // Solo arrancamos si el usuario ya activó el micro
//                 if (audioContextRef.current) startGameFlow(endsAt); 
//             }, msUntilStart);

//         } else {
//             // Caso 2: Llegamos tarde (Late Join) o justo a tiempo -> Iniciar Directo
//             if (audioContextRef.current) {
//                 startGameFlow(endsAt);
//             } else {
//                 setGameState('PERMISSION_NEEDED');
//             }
//         }

//     }, [config, userZone]);

//     // -------------------------------------------------------------------------
//     // 2. REACCIÓN A LOS RESULTADOS (FIN DE PARTIDA)
//     // -------------------------------------------------------------------------
//     useEffect(() => {
//         if (!winners) return;

//         stopAllProcesses();
//         setGameState('FINISHED');

//         const wZone = winners.winnerZone || winners.WinnerZone;
        
//         if (wZone === 'tie') {
//             setWinnerMessage('¡EMPATE!');
//         } else if (wZone?.toLowerCase() === userZone?.toLowerCase()) {
//             setWinnerMessage('¡GANAMOS! 🏆');
//         } else {
//             setWinnerMessage(`GANÓ ${wZone?.toUpperCase()}`);
//         }

//     }, [winners, userZone]);


//     // -------------------------------------------------------------------------
//     // LÓGICA DE AUDIO (WEB AUDIO API)
//     // -------------------------------------------------------------------------
//     const initMicrophone = async () => {
//         try {
//             const AudioContext = window.AudioContext || window.webkitAudioContext;
//             audioContextRef.current = new AudioContext();
            
//             const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
//             const source = audioContextRef.current.createMediaStreamSource(stream);
            
//             // Configurar Analizador
//             analyserRef.current = audioContextRef.current.createAnalyser();
//             analyserRef.current.fftSize = 256; 
//             analyserRef.current.smoothingTimeConstant = 0.5;

//             // Filtro Pasa-Banda (1kHz) para capturar voz y eliminar soplidos/viento
//             const filter = audioContextRef.current.createBiquadFilter();
//             filter.type = 'bandpass';
//             filter.frequency.value = 1000; 

//             source.connect(filter);
//             filter.connect(analyserRef.current);

//             // Una vez activado, verificamos si debemos entrar al juego inmediatamente
//             if (config) {
//                 const now = Date.now();
//                 const startAt = new Date(config.startAt).getTime();
//                 const endsAt = new Date(config.endsAt).getTime();

//                 if (now < startAt) {
//                     startCountdown(Math.ceil((startAt - now) / 1000));
//                 } else {
//                     // Si ya pasó el tiempo de inicio, entramos directo
//                     startGameFlow(endsAt);
//                 }
//             } else {
//                 setGameState('WAITING_SIGNAL');
//             }

//         } catch (err) {
//             console.error("Error al acceder al micrófono:", err);
//             alert("Necesitamos acceso al micrófono para poder medir tu grito.");
//         }
//     };

//     // -------------------------------------------------------------------------
//     // CONTROL DE FLUJO Y TIMERS
//     // -------------------------------------------------------------------------

//     // Cuenta regresiva inicial (3..2..1)
//     const startCountdown = (seconds) => {
//         setGameState('COUNTDOWN');
//         setCountdownVal(seconds);
        
//         if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        
//         let current = seconds;
//         countdownIntervalRef.current = setInterval(() => {
//             current--;
//             setCountdownVal(current);
//             if (current <= 0) clearInterval(countdownIntervalRef.current);
//         }, 1000);
//     };

//     // Reloj del juego (cuenta regresiva de duración)
//     const startGameTimer = (endsAtTimestamp) => {
//         if (gameTimerRef.current) clearInterval(gameTimerRef.current);
        
//         // Ejecución inmediata para no esperar 1seg al primer render
//         const updateTimer = () => {
//             const now = Date.now();
//             const diff = Math.ceil((endsAtTimestamp - now) / 1000);
//             setTimeLeft(diff > 0 ? diff : 0);
//             if (diff <= 0) clearInterval(gameTimerRef.current);
//         };

//         updateTimer();
//         gameTimerRef.current = setInterval(updateTimer, 1000);
//     };

//     // Inicia toda la maquinaria del juego
//     const startGameFlow = (endsAtTimestamp) => {
//         if (!analyserRef.current) return;
        
//         setGameState('RUNNING');
        
//         // 1. Iniciar Timer Visual
//         if (endsAtTimestamp) startGameTimer(endsAtTimestamp);

//         // 2. Iniciar Loop de Audio (Visualización + Acumulación)
//         processAudioLoop();

//         // 3. Iniciar Envío de Reportes (Cada 500ms)
//         if (reportingInterval.current) clearInterval(reportingInterval.current);
//         reportingInterval.current = setInterval(sendReport, 500);
//     };

//     // Ciclo de renderizado de audio (60fps aprox)
//     const processAudioLoop = () => {
//         if (!analyserRef.current) return;

//         const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
//         analyserRef.current.getByteFrequencyData(dataArray);

//         // Calcular promedio de frecuencias del frame actual
//         let sum = 0;
//         for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
//         let average = sum / dataArray.length;

//         // Ajuste visual local para que la barra se mueva más divertido en el celular
//         // Restamos un "piso de ruido" (15) y multiplicamos
//         let visualVal = Math.max(0, (average - 15)) * 2.0; 
//         if (visualVal > 100) visualVal = 100;

//         setVolumeDisplay(visualVal);

//         // Acumulamos el valor para el promedio que se enviará al server
//         volumeAccumulator.current += visualVal; 
//         volumeReadings.current += 1;

//         animationRef.current = requestAnimationFrame(processAudioLoop);
//     };

//     // Envío de datos al Backend
//     const sendReport = () => {
//         // Si no hay lecturas o no hay config, no enviamos nada
//         if (volumeReadings.current === 0 || !config?.matchId) return;

//         // Promedio del intervalo
//         const avgVolume = volumeAccumulator.current / volumeReadings.current;
        
//         // Llamada al BL
//         BLSendNoiseReport(config.matchId, userZone, avgVolume);

//         // Resetear acumuladores para el siguiente intervalo
//         volumeAccumulator.current = 0;
//         volumeReadings.current = 0;
//     };

//     const stopAllProcesses = () => {
//         if (reportingInterval.current) clearInterval(reportingInterval.current);
//         if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
//         if (gameTimerRef.current) clearInterval(gameTimerRef.current);
//         if (animationRef.current) cancelAnimationFrame(animationRef.current);
//     };

//     // -------------------------------------------------------------------------
//     // RENDERIZADO
//     // -------------------------------------------------------------------------
//     return (
//         <div className="noisemeter">
//             <div className="noisemeter__card">
                
//                 {/* 1. PERMISO */}
//                 {gameState === 'PERMISSION_NEEDED' && (
//                     <div className="noisemeter__step">
//                         <i className="bi bi-mic-fill" style={{fontSize: '3rem', color: '#666', marginBottom: '1rem'}}></i>
//                         <h2 className="noisemeter__title">¡Prepara tu voz!</h2>
//                         <p className="noisemeter__text">Activa tu micrófono para sumar puntos a tu zona.</p>
//                         <button className="noisemeter__btn" onClick={initMicrophone}>
//                             ACTIVAR MICRÓFONO
//                         </button>
//                     </div>
//                 )}

//                 {/* 2. CONECTADO (Esperando al Admin) */}
//                 {gameState === 'WAITING_SIGNAL' && (
//                     <div className="noisemeter__step noisemeter__step--waiting">
//                         <div className="noisemeter__pulse-icon">📡</div>
//                         <h2>Conectado</h2>
//                         <p>Espera la señal del estadio...</p>
//                         <small className="text-muted">Tu zona: {userZone}</small>
//                     </div>
//                 )}

//                 {/* 3. BLOQUEADO (Zona incorrecta) */}
//                 {gameState === 'LOCKED' && (
//                     <div className="noisemeter__step">
//                         <i className="bi bi-hourglass-split" style={{fontSize: '3rem', color: '#999', marginBottom: '1rem'}}></i>
//                         <h2>Espera tu turno</h2>
//                         <p>Esta ronda es para la otra zona.</p>
//                     </div>
//                 )}

//                 {/* 4. CUENTA REGRESIVA */}
//                 {gameState === 'COUNTDOWN' && (
//                     <div className="noisemeter__step">
//                         <p className="noisemeter__label">Prepárate...</p>
//                         <span className="noisemeter__countdown-number">{countdownVal}</span>
//                     </div>
//                 )}

//                 {/* 5. JUGANDO (RUNNING) */}
//                 {gameState === 'RUNNING' && (
//                     <div className="noisemeter__step">
//                         <h2 className="noisemeter__title" style={{color: '#dc3545', fontSize: '2.5rem', margin: '0'}}>¡GRITA!</h2>
                        
//                         {/* Timer Visual */}
//                         <div className="noisemeter__timer" style={{fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '15px', color: '#555'}}>
//                             ⏱️ {timeLeft}s
//                         </div>
                        
//                         <div className="noisemeter__meter-container">
//                             <div 
//                                 className={`noisemeter__meter-fill`} 
//                                 style={{ 
//                                     width: `${volumeDisplay}%`,
//                                     // Cambia de color según intensidad: Verde -> Amarillo -> Rojo
//                                     background: `linear-gradient(90deg, #4caf50, ${volumeDisplay > 70 ? '#f44336' : '#ffeb3b'})`
//                                 }}
//                             ></div>
//                         </div>

//                         <span className="noisemeter__score-number">{Math.round(volumeDisplay)}</span>
//                         <p className="noisemeter__label">INTENSIDAD</p>
//                     </div>
//                 )}

//                 {/* 6. FINALIZADO */}
//                 {gameState === 'FINISHED' && (
//                     <div className="noisemeter__step">
//                         <h2 style={{marginTop:'20px'}}>¡TIEMPO!</h2>
                        
//                         <div style={{margin: '30px 0'}}>
//                             <h1 style={{fontSize: '2rem', color: '#0d6efd', fontWeight: '800'}}>
//                                 {winnerMessage || "Calculando..."}
//                             </h1>
//                         </div>
                        
//                         <p className="noisemeter__text">Mira los resultados en la pantalla gigante.</p>
//                     </div>
//                 )}

//             </div>
//         </div>
//     );
// };

// export { Noisemeter };



























// const Noisemeter = ({ config, winners, userZone }) => {
    
//     // --- ESTADOS VISUALES ---
//     const [gameState, setGameState] = useState('PERMISSION_NEEDED'); 
//     // Posibles estados: 'PERMISSION_NEEDED', 'WAITING_SIGNAL', 'LOCKED', 'COUNTDOWN', 'RUNNING', 'FINISHED'
    
//     const [volumeDisplay, setVolumeDisplay] = useState(0); // 0-100 para la barra local
//     const [countdownVal, setCountdownVal] = useState(3);
//     const [winnerMessage, setWinnerMessage] = useState(null);

//     // --- REFERENCIAS (Audio & Timers) ---
//     const audioContextRef = useRef(null);
//     const analyserRef = useRef(null);
//     const volumeAccumulator = useRef(0);
//     const volumeReadings = useRef(0);
//     const reportingInterval = useRef(null);
//     const animationRef = useRef(null);
//     const countdownIntervalRef = useRef(null);

//     // Limpieza al desmontar
//     useEffect(() => {
//         return () => stopAllProcesses();
//     }, []);

//     // --- 1. REACCIÓN A CAMBIOS DE CONFIGURACIÓN (Inicio) ---
//     useEffect(() => {
//         if (!config) return;

//         // A) Validar si mi zona participa (Guerra o Turno Específico)
//         if (config.targetZone && config.targetZone !== 'all' && config.targetZone.toLowerCase() !== userZone?.toLowerCase()) {
//             stopAllProcesses();
//             setGameState('LOCKED');
//             return;
//         }

//         // B) Gestión del Tiempo (Countdown vs Running)
//         const now = Date.now();
//         const startAt = new Date(config.startAt).getTime();
//         const msUntilStart = startAt - now;

//         if (msUntilStart > 0) {
//             // Todavía falta para empezar -> Mostrar cuenta regresiva
//             // Si ya tenemos permiso de audio, pasamos a Countdown, si no, pedimos permiso.
//             if (audioContextRef.current) {
//                 startCountdown(Math.ceil(msUntilStart / 1000));
//             } else {
//                 setGameState('PERMISSION_NEEDED'); // Pedirá permiso y luego iniciará countdown
//             }
            
//             // Programar el inicio automático
//             const timeoutId = setTimeout(() => {
//                 if (audioContextRef.current) startGameFlow();
//             }, msUntilStart);
            
//             return () => clearTimeout(timeoutId);

//         } else {
//             // Ya empezó el juego (Late join)
//             if (audioContextRef.current) {
//                 startGameFlow();
//             } else {
//                 setGameState('PERMISSION_NEEDED');
//             }
//         }

//     }, [config, userZone]);

//     // --- 2. REACCIÓN A GANADORES (Fin) ---
//     useEffect(() => {
//         if (!winners) return;

//         stopAllProcesses();
//         setGameState('FINISHED');

//         const wZone = winners.winnerZone || winners.WinnerZone; // Ajustar según venga del DTO
        
//         if (wZone === 'tie') {
//             setWinnerMessage('¡EMPATE!');
//         } else if (wZone?.toLowerCase() === userZone?.toLowerCase()) {
//             setWinnerMessage('¡GANAMOS! 🏆');
//         } else {
//             setWinnerMessage(`GANÓ ${wZone?.toUpperCase()}`);
//         }

//     }, [winners, userZone]);


//     // --- LÓGICA INTERNA ---

//     const initMicrophone = async () => {
//         try {
//             const AudioContext = window.AudioContext || window.webkitAudioContext;
//             audioContextRef.current = new AudioContext();
            
//             const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
//             const source = audioContextRef.current.createMediaStreamSource(stream);
            
//             // Analizador
//             analyserRef.current = audioContextRef.current.createAnalyser();
//             analyserRef.current.fftSize = 256;
//             analyserRef.current.smoothingTimeConstant = 0.5;

//             // Filtro de voz (Bandpass 1kHz) para limpiar ruido grave
//             const filter = audioContextRef.current.createBiquadFilter();
//             filter.type = 'bandpass';
//             filter.frequency.value = 1000; 

//             source.connect(filter);
//             filter.connect(analyserRef.current);

//             // Una vez activo, decidimos qué mostrar según la config actual
//             if (config) {
//                 const now = Date.now();
//                 const startAt = new Date(config.startAt).getTime();
//                 if (now < startAt) {
//                     startCountdown(Math.ceil((startAt - now) / 1000));
//                 } else {
//                     startGameFlow();
//                 }
//             } else {
//                 setGameState('WAITING_SIGNAL');
//             }

//         } catch (err) {
//             console.error("Error micrófono", err);
//             alert("Necesitamos acceso al micrófono para medir tu grito.");
//         }
//     };

//     const startCountdown = (seconds) => {
//         setGameState('COUNTDOWN');
//         setCountdownVal(seconds);
        
//         if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        
//         let current = seconds;
//         countdownIntervalRef.current = setInterval(() => {
//             current--;
//             setCountdownVal(current);
//             if (current <= 0) clearInterval(countdownIntervalRef.current);
//         }, 1000);
//     };

//     const startGameFlow = () => {
//         if (!analyserRef.current) return;
        
//         setGameState('RUNNING');
        
//         // 1. Loop Visual (60fps)
//         processAudioLoop();

//         // 2. Loop de Reporte (2Hz - cada 500ms)
//         if (reportingInterval.current) clearInterval(reportingInterval.current);
//         reportingInterval.current = setInterval(sendReport, 500);
//     };

//     const processAudioLoop = () => {
//         if (!analyserRef.current) return;

//         const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
//         analyserRef.current.getByteFrequencyData(dataArray);

//         // Calcular promedio del frame
//         let sum = 0;
//         for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
//         let average = sum / dataArray.length;

//         // Lógica visual local (ajustar sensibilidad visual para que se vea divertido)
//         let visualVal = Math.max(0, (average - 15)) * 2.0; 
//         if (visualVal > 100) visualVal = 100;

//         setVolumeDisplay(visualVal);

//         // Acumular para el reporte promedio al servidor
//         // Usamos el valor crudo 'average' o ajustado según prefieras enviar
//         volumeAccumulator.current += visualVal; 
//         volumeReadings.current += 1;

//         animationRef.current = requestAnimationFrame(processAudioLoop);
//     };

//     const sendReport = () => {
//         if (volumeReadings.current === 0 || !config?.matchId) return;

//         const avgVolume = volumeAccumulator.current / volumeReadings.current;
        
//         // Enviar al BL
//         BLSendNoiseReport(config.matchId, userZone, avgVolume);

//         // Reset
//         volumeAccumulator.current = 0;
//         volumeReadings.current = 0;
//     };

//     const stopAllProcesses = () => {
//         if (reportingInterval.current) clearInterval(reportingInterval.current);
//         if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
//         if (animationRef.current) cancelAnimationFrame(animationRef.current);
        
//         // Opcional: Cerrar contexto de audio si quieres liberar recursos
//         // if (audioContextRef.current) audioContextRef.current.close();
//     };

//     // --- RENDERIZADO ---

//     return (
//         <div className="noisemeter">
//             <div className="noisemeter__card">
                
//                 {/* 1. PEDIR PERMISO */}
//                 {gameState === 'PERMISSION_NEEDED' && (
//                     <div className="noisemeter__step">
//                         <i className="bi bi-mic-fill" style={{fontSize: '3rem', color: '#666'}}></i>
//                         <h2 className="noisemeter__title">¡Prepara tu voz!</h2>
//                         <p className="noisemeter__text">Activa tu micrófono para sumar puntos.</p>
//                         <button className="noisemeter__btn" onClick={initMicrophone}>
//                             ACTIVAR MICRÓFONO
//                         </button>
//                     </div>
//                 )}

//                 {/* 2. ESPERANDO SEÑAL (Ya con permiso pero sin config) */}
//                 {gameState === 'WAITING_SIGNAL' && (
//                     <div className="noisemeter__step noisemeter__step--waiting">
//                         <div className="noisemeter__pulse-icon">📡</div>
//                         <h2>Conectado</h2>
//                         <p>Espera la señal del estadio...</p>
//                     </div>
//                 )}

//                 {/* 3. BLOQUEADO (Otra zona jugando) */}
//                 {gameState === 'LOCKED' && (
//                     <div className="noisemeter__step">
//                         <i className="bi bi-hourglass-split" style={{fontSize: '3rem', color: '#999'}}></i>
//                         <h2>Espera tu turno</h2>
//                         <p>Esta ronda es para la otra zona.</p>
//                     </div>
//                 )}

//                 {/* 4. CUENTA REGRESIVA */}
//                 {gameState === 'COUNTDOWN' && (
//                     <div className="noisemeter__step">
//                         <p className="noisemeter__label">Prepárate...</p>
//                         <span className="noisemeter__countdown-number">{countdownVal}</span>
//                     </div>
//                 )}

//                 {/* 5. JUGANDO */}
//                 {gameState === 'RUNNING' && (
//                     <div className="noisemeter__step">
//                         <h2 className="noisemeter__title" style={{color: '#dc3545', fontSize: '2rem'}}>¡GRITA!</h2>
                        
//                         <div className="noisemeter__meter-container">
//                             <div 
//                                 className={`noisemeter__meter-fill`} 
//                                 style={{ 
//                                     width: `${volumeDisplay}%`,
//                                     background: `linear-gradient(90deg, #4caf50, ${volumeDisplay > 70 ? '#f44336' : '#ffeb3b'})`
//                                 }}
//                             ></div>
//                         </div>

//                         <span className="noisemeter__score-number">{Math.round(volumeDisplay)}</span>
//                         <p className="noisemeter__label">INTENSIDAD</p>
//                     </div>
//                 )}

//                 {/* 6. FINALIZADO */}
//                 {gameState === 'FINISHED' && (
//                     <div className="noisemeter__step">
//                         <h2>¡TIEMPO!</h2>
//                         <h1 style={{fontSize: '2.5rem', color: '#0d6efd', margin: '20px 0'}}>{winnerMessage}</h1>
//                         <p className="noisemeter__text">Mira los resultados en la pantalla.</p>
//                     </div>
//                 )}

//             </div>
//         </div>
//     );
// };

// export { Noisemeter };














// const Noisemeter = () => {
//     // --- ESTADOS DE LA INTERFAZ (MAQUINA DE ESTADOS) ---
//     // 'PERMISSION_NEEDED' | 'WAITING_FOR_ADMIN' | 'COUNTDOWN' | 'GAME_ACTIVE' | 'FINISHED'
//     const [gameState, setGameState] = useState('PERMISSION_NEEDED');

//     // --- ESTADOS DEL JUEGO ---
//     const [volumeLevel, setVolumeLevel] = useState(0);         // Nivel de ruido (0-100)
//     const [countdownVal, setCountdownVal] = useState(3);       // Cuenta regresiva inicial (3, 2, 1)
//     const [timeLeft, setTimeLeft] = useState(0);               // Tiempo restante de la dinámica
//     const [teamColor, setTeamColor] = useState('blue');        // 'blue' u 'gold' (Simulado desde sessionStorage)

//     // --- REFERENCIAS (Para mantener valores sin re-renderizar) ---
//     const audioContextRef = useRef(null);
//     const analyserRef = useRef(null);
//     const animationFrameRef = useRef(null);
//     const gameIntervalRef = useRef(null);

//     // --- CONFIGURACIÓN DE AUDIO (Hardcoded o dinámica) ---
//     const AUDIO_CONFIG = {
//         floorNoise: 25,   // Ruido ambiente a ignorar
//         multiplier: 1.8,  // Sensibilidad para llegar a 100
//         fftSize: 512      // Resolución del análisis
//     };

//     // --- EFECTO: CARGA INICIAL ---
//     useEffect(() => {
//         // 1. Simular obtener el equipo del storage
//         const storedTeam = sessionStorage.getItem('user_team') || 'blue';
//         setTeamColor(storedTeam);

//         // 2. EXPOSE PARA PRUEBAS: Creamos una función global para simular al Admin
//         // Escribe en la consola del navegador: window.simulateAdminStart(30)
//         window.simulateAdminStart = (durationInSeconds = 30) => {
//             console.log("Admin ha iniciado la dinámica...");
//             startCountdownSequence(durationInSeconds);
//         };

//         // 3. Limpieza al desmontar el componente
//         return () => {
//             stopAudioProcess();
//             cancelAnimationFrame(animationFrameRef.current);
//             clearInterval(gameIntervalRef.current);
//         };
//     }, []);

//     // --- LÓGICA: INICIALIZAR MICRÓFONO ---
//     const handleRequestMicrophonePermission = async () => {
//         try {
//             // 1. Creamos el contexto de audio (Soporte cross-browser)
//             const AudioContext = window.AudioContext || window.webkitAudioContext;
//             audioContextRef.current = new AudioContext();

//             // 2. Solicitamos el stream del micrófono
//             const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
//             // 3. Configuramos los nodos de audio
//             const source = audioContextRef.current.createMediaStreamSource(stream);
//             analyserRef.current = audioContextRef.current.createAnalyser();
//             analyserRef.current.fftSize = AUDIO_CONFIG.fftSize;
//             analyserRef.current.smoothingTimeConstant = 0.6; // Suavizado visual

//             // 4. Filtro Pasa-Banda (Anti-Soplidos / Anti-Cheat)
//             // Filtramos frecuencias bajas (viento) y muy altas
//             const filterNode = audioContextRef.current.createBiquadFilter();
//             filterNode.type = 'bandpass';
//             filterNode.frequency.value = 1000; // Frecuencia central voz humana
//             filterNode.Q.value = 0.7;

//             // 5. Conexiones: Fuente -> Filtro -> Analizador
//             source.connect(filterNode);
//             filterNode.connect(analyserRef.current);

//             // 6. Cambiamos estado a "Esperando al Admin"
//             setGameState('WAITING_FOR_ADMIN');
//             console.log("Micrófono activo. Esperando señal...");

//             // Nota: No iniciamos el loop de dibujo todavía para ahorrar recursos
//             // hasta que el juego empiece.

//         } catch (error) {
//             console.error("Error al acceder al micrófono:", error);
//             alert("Necesitamos acceso al micrófono para que puedas jugar.");
//         }
//     };

//     // --- LÓGICA: SECUENCIA DE INICIO (3, 2, 1) ---
//     const startCountdownSequence = (duration) => {
//         setGameState('COUNTDOWN');
//         setCountdownVal(3); // Reiniciar cuenta

//         let count = 3;
//         const countInterval = setInterval(() => {
//             count--;
//             if (count > 0) {
//                 setCountdownVal(count);
//             } else {
//                 // Fin de la cuenta regresiva, inicia el juego
//                 clearInterval(countInterval);
//                 startGameLoop(duration);
//             }
//         }, 1000); // Cada 1 segundo
//     };

//     // --- LÓGICA: BUCLE DEL JUEGO ---
//     const startGameLoop = (duration) => {
//         setGameState('GAME_ACTIVE');
//         setTimeLeft(duration);

//         // 1. Iniciar el loop de análisis de audio (requestAnimationFrame)
//         processAudioSignal();

//         // 2. Iniciar el temporizador de la dinámica
//         gameIntervalRef.current = setInterval(() => {
//             setTimeLeft((prevTime) => {
//                 if (prevTime <= 1) {
//                     finishGame(); // Se acabó el tiempo
//                     return 0;
//                 }
//                 return prevTime - 1;
//             });
//         }, 1000);
//     };

//     // --- LÓGICA: PROCESAMIENTO DE AUDIO (60 FPS) ---
//     const processAudioSignal = () => {
//         // Verificar que el analizador exista y el juego siga activo
//         if (!analyserRef.current) return;

//         const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
//         analyserRef.current.getByteFrequencyData(dataArray);

//         // 1. Calcular promedio
//         let sum = 0;
//         for (let i = 0; i < dataArray.length; i++) {
//             sum += dataArray[i];
//         }
//         let average = sum / dataArray.length;

//         // 2. Aplicar matemáticas de calibración (Piso de ruido y Multiplicador)
//         let adjustedAverage = Math.max(0, average - AUDIO_CONFIG.floorNoise);
//         let calculatedVolume = Math.round(adjustedAverage * AUDIO_CONFIG.multiplier);

//         // 3. Limitar a 100
//         if (calculatedVolume > 100) calculatedVolume = 100;

//         // 4. Actualizar estado visual
//         setVolumeLevel(calculatedVolume);

//         // 5. Loop (Recursión)
//         // Solo continuamos si el estado sigue siendo ACTIVE (o COUNTDOWN por seguridad)
//         animationFrameRef.current = requestAnimationFrame(processAudioSignal);
//     };

//     // --- LÓGICA: FINALIZAR JUEGO ---
//     const finishGame = () => {
//         setGameState('FINISHED');
//         stopAudioProcess();
//     };

//     const stopAudioProcess = () => {
//         if (gameIntervalRef.current) clearInterval(gameIntervalRef.current);
//         if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
//         setVolumeLevel(0);
//     };

//     // --- RENDERIZADO CONDICIONAL ---
//     const renderContent = () => {
//         switch (gameState) {
//             case 'PERMISSION_NEEDED':
//                 return (
//                     <div className="noisemeter__step noisemeter__step--permission">
//                         <h2 className="noisemeter__title">¡Tu equipo te necesita!</h2>
//                         <p className="noisemeter__text">Para sumar puntos, necesitamos escuchar tu grito.</p>
//                         <button className="noisemeter__btn" onClick={handleRequestMicrophonePermission} type="button">
//                             🎙️ Activar Micrófono
//                         </button>
//                     </div>
//                 );

//             case 'WAITING_FOR_ADMIN':
//                 return (
//                     <div className="noisemeter__step noisemeter__step--waiting">
//                         <div className="noisemeter__pulse-icon">📡</div>
//                         <h2 className="noisemeter__title">¡Atento!</h2>
//                         <p className="noisemeter__text">Estamos a punto de comenzar...</p>
//                         <small className="noisemeter__hint">(Espera la señal del estadio)</small>
//                     </div>
//                 );

//             case 'COUNTDOWN':
//                 return (
//                     <div className="noisemeter__step noisemeter__step--countdown">
//                         <p className="noisemeter__text">Prepárate para gritar en...</p>
//                         <span className="noisemeter__countdown-number">{countdownVal}</span>
//                     </div>
//                 );

//             case 'GAME_ACTIVE':
//                 return (
//                     <div className="noisemeter__step noisemeter__step--active">
//                         <div className="noisemeter__info-bar">
//                             <span className="noisemeter__timer">⏳ {timeLeft}s</span>
//                             <span className="noisemeter__zone-badge">Zona {teamColor}</span>
//                         </div>
                        
//                         <div className="noisemeter__meter-container">
//                             <div className={`noisemeter__meter-fill noisemeter__meter-fill--${teamColor}`} style={{ width: `${volumeLevel}%` }}></div>
//                         </div>

//                         <span className="noisemeter__score-number">{volumeLevel}</span>
//                         <p className="noisemeter__label">INTENSIDAD</p>
//                     </div>
//                 );

//             case 'FINISHED':
//                 return (
//                     <div className="noisemeter__step noisemeter__step--finished">
//                         <h2 className="noisemeter__title">¡Muy bien! 🎉</h2>
//                         <p className="noisemeter__text">Lo has hecho increíble.</p>
//                         <p className="noisemeter__text">Mira la pantalla gigante para los resultados.</p>
//                     </div>
//                 );

//             default:
//                 return null;
//         }
//     };

//     return (
//         <div className="noisemeter">
//             <div className="noisemeter__card">
//                 {renderContent()}
                
//                 {/* Debug Helper: Solo visible si lo deseas */}
//                 {gameState === 'WAITING_FOR_ADMIN' && (
//                     <button onClick={() => window.simulateAdminStart(15)} style={{marginTop: '50px', opacity: 0.5, fontSize: '10px'}}>
//                         [DEBUG: Simular Admin Start]
//                     </button>
//                 )}
//             </div>
//         </div>
//     );
// };

// export { Noisemeter };









// const Noisemeter = () => {
//     return (
//         <p>Noisemeter Aficionad@</p>
//     )
// }

// export { Noisemeter };